(() => {
  const VERSION = '2026-09-14-progress-1.2.3-visual-artifact-fix';
  const BASE_FILENAME = 'fp-product-1-2-2-backup.js';

  const findSelfSrc = () => {
    if (document.currentScript?.src) return document.currentScript.src;
    const script = Array.from(document.scripts).find((item) => /\/fp-product\.js(?:[?#]|$)/.test(item.src || ''));
    return script?.src || '';
  };

  const loadBase = () => {
    if (window.__PMS_FP_PRODUCT_1_2_2_BACKUP_LOADING__) return;
    const selfSrc = findSelfSrc();
    if (!selfSrc) return;

    window.__PMS_FP_PRODUCT_1_2_2_BACKUP_LOADING__ = true;
    const script = document.createElement('script');
    const baseUrl = new URL(BASE_FILENAME, selfSrc);
    baseUrl.search = new URL(selfSrc).search;
    script.src = baseUrl.href;
    script.async = false;
    script.dataset.pmsFpProductBase = VERSION;
    script.onload = () => {
      window.__PMS_FP_PRODUCT_1_2_2_BACKUP_LOADED__ = true;
      window.__PMS_FP_PRODUCT_1_2_2_BACKUP_LOADING__ = false;
    };
    script.onerror = () => {
      window.__PMS_FP_PRODUCT_1_2_2_BACKUP_LOADING__ = false;
      console.error('[PMS] Unable to load 1.2.2 fp-product backup.');
    };
    document.head.appendChild(script);
  };

  const normalizeText = (node) => String(node?.textContent || '').replace(/\s+/g, ' ').trim();

  const installStyle = () => {
    if (document.getElementById('pms-upload-artifact-1-2-3-style')) return;
    const style = document.createElement('style');
    style.id = 'pms-upload-artifact-1-2-3-style';
    style.textContent = `
      [data-fp-pdp] .pms-tee-upload-field .tee-field__heading::before {
        content: none !important;
        display: none !important;
      }

      [data-fp-pdp] .pms-tee-upload-field [data-pms-upload-heading-clean] {
        position: relative !important;
        isolation: isolate !important;
      }

      [data-fp-pdp] .pms-tee-upload-field [data-pms-upload-heading-clean]::after {
        content: "" !important;
        display: block !important;
        position: absolute !important;
        left: 50% !important;
        top: 100% !important;
        transform: translateX(-50%) !important;
        width: 52px !important;
        height: var(--pms-upload-mask-height, 20px) !important;
        background: var(--pms-upload-mask-bg, #fff) !important;
        pointer-events: none !important;
        z-index: 2147480000 !important;
      }

      [data-fp-pdp] .pms-tee-upload-field [contenteditable="true"]:empty {
        caret-color: transparent !important;
      }
    `;
    document.head.appendChild(style);
  };

  const findHeading = (field) => {
    const preferred = field.querySelector('.tee-field__heading');
    if (preferred && /Upload Your Photo/i.test(normalizeText(preferred))) return preferred;

    const candidates = Array.from(field.querySelectorAll('h1,h2,h3,h4,h5,h6,p,div,span,label'))
      .filter((node) => normalizeText(node) === 'Upload Your Photo');

    return candidates.sort((a, b) => a.children.length - b.children.length)[0] || null;
  };

  const findControl = (field) => {
    return Array.from(field.querySelectorAll('button,a,[role="button"],label,div,span'))
      .filter((node) => /^(Uploading\b|Choose another image$|Upload image$)/i.test(normalizeText(node)))
      .sort((a, b) => normalizeText(a).length - normalizeText(b).length)[0] || null;
  };

  const pickBackground = (heading, field) => {
    let node = heading.parentElement;
    while (node && node !== field.parentElement) {
      const background = window.getComputedStyle(node).backgroundColor;
      if (background && background !== 'rgba(0, 0, 0, 0)' && background !== 'transparent') return background;
      node = node.parentElement;
    }
    return '#fff';
  };

  const cleanField = (field) => {
    if (!field?.isConnected) return;

    const heading = findHeading(field);
    if (!heading) return;
    const control = findControl(field);

    const headingRect = heading.getBoundingClientRect();
    const controlRect = control?.getBoundingClientRect();
    const headingCenter = (headingRect.left + headingRect.right) / 2;

    if (!heading.hasAttribute('data-pms-upload-heading-clean')) {
      heading.dataset.pmsUploadHeadingClean = '';
    }

    const availableGap = Math.max(
      8,
      (controlRect?.top || headingRect.bottom + 24) - headingRect.bottom - 1
    );
    const maskHeight = Math.min(24, availableGap);
    const maskHeightValue = `${maskHeight}px`;
    const maskBackground = pickBackground(heading, field);

    if (heading.style.getPropertyValue('--pms-upload-mask-height') !== maskHeightValue) {
      heading.style.setProperty('--pms-upload-mask-height', maskHeightValue);
    }
    if (heading.style.getPropertyValue('--pms-upload-mask-bg') !== maskBackground) {
      heading.style.setProperty('--pms-upload-mask-bg', maskBackground);
    }

    field.querySelectorAll('*').forEach((node) => {
      if (node === heading || node.contains(heading) || heading.contains(node)) return;
      if (node.closest('button,a,label,input,textarea,select,option,[role="button"],.pms-ai-progress-v12')) return;

      const rect = node.getBoundingClientRect();
      if ((!rect.width && !rect.height) || rect.width > 42 || rect.height > 42) return;

      const center = (rect.left + rect.right) / 2;
      const zoneBottom = (controlRect?.top || headingRect.bottom + 40) + 2;
      const inZone =
        rect.top >= headingRect.bottom - 7 &&
        rect.bottom <= zoneBottom &&
        Math.abs(center - headingCenter) <= 85;

      if (!inZone) return;

      const text = normalizeText(node);
      const before = window.getComputedStyle(node, '::before').content;
      const after = window.getComputedStyle(node, '::after').content;
      const pseudo = [before, after].some((value) =>
        value && !['none', 'normal', '""', "''"].includes(value)
      );
      const emptyish = !text || /^[iIl|丨]$/.test(text);
      const iconLike = ['I', 'SVG', 'PATH', 'USE'].includes(node.tagName);

      if ((emptyish || pseudo || iconLike) && window.getComputedStyle(node).display !== 'none') {
        node.style.setProperty('display', 'none', 'important');
      }
    });
  };

  const FIELD_SELECTOR = '.watercolor-ai-personalizer .pms-tee-upload-field, .watercolor-ai-personalizer .tee-field--photo';
  const observerByPersonalizer = new WeakMap();
  const pendingRoots = new Set();
  let cleanFrame = 0;

  const cleanAll = (scope) => {
    installStyle();
    const root = scope?.nodeType === Node.ELEMENT_NODE ? scope : document;
    const fields = new Set();
    const closestField = root.closest?.(FIELD_SELECTOR);
    if (closestField) fields.add(closestField);
    if (root.matches?.(FIELD_SELECTOR)) fields.add(root);
    root.querySelectorAll?.(FIELD_SELECTOR).forEach((field) => fields.add(field));
    fields.forEach(cleanField);
  };

  const scheduleClean = (scope) => {
    if (scope) pendingRoots.add(scope);
    if (cleanFrame) return;
    cleanFrame = window.requestAnimationFrame(() => {
      cleanFrame = 0;
      const roots = Array.from(pendingRoots);
      pendingRoots.clear();
      roots.forEach(cleanAll);
    });
  };

  const observePersonalizer = (personalizer) => {
    if (!personalizer?.isConnected || observerByPersonalizer.has(personalizer)) return;
    cleanAll(personalizer);
    const observer = new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) scheduleClean(node);
        });
      });
    });
    observer.observe(personalizer, { childList: true, subtree: true });
    observerByPersonalizer.set(personalizer, observer);
  };

  const observeScope = (scope = document) => {
    if (scope.matches?.('.watercolor-ai-personalizer')) observePersonalizer(scope);
    scope.querySelectorAll?.('[data-fp-pdp] .watercolor-ai-personalizer').forEach(observePersonalizer);
  };

  const disconnectScope = (scope) => {
    const personalizers = [];
    if (scope?.matches?.('.watercolor-ai-personalizer')) personalizers.push(scope);
    scope?.querySelectorAll?.('.watercolor-ai-personalizer').forEach((node) => personalizers.push(node));
    personalizers.forEach((personalizer) => {
      observerByPersonalizer.get(personalizer)?.disconnect();
      observerByPersonalizer.delete(personalizer);
    });
  };

  const start = () => {
    if (window.__PMS_UPLOAD_ARTIFACT_FIX_1_2_3__) return;
    window.__PMS_UPLOAD_ARTIFACT_FIX_1_2_3__ = true;

    installStyle();
    observeScope();
    document.addEventListener('shopify:section:load', (event) => observeScope(event.target));
    document.addEventListener('shopify:section:unload', (event) => disconnectScope(event.target));
  };

  loadBase();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
